if (!customElements.get('shoppy-reviews')) {
  customElements.define(
    'shoppy-reviews',
    class ShoppyReviews extends HTMLElement {
      connectedCallback() {
        if (this._ready || typeof Splide === 'undefined') return;

        const track = this.querySelector('.shoppy-reviews__viewport');
        const list = this.querySelector('.shoppy-reviews__track');
        if (!track || !list || !list.children.length) return;

        this._ready = true;
        this._inView = true;
        this._modalOpen = false;
        this._modalIndex = 0;
        this._modalMuted = false;

        this.prevBtn = this.querySelector('.shoppy-reviews__nav--prev');
        this.nextBtn = this.querySelector('.shoppy-reviews__nav--next');
        this.modal = this.querySelector('.shoppy-reviews-modal');
        this.modalVideo = this.modal?.querySelector('.shoppy-reviews-modal__video');
        this.progressEl = this.modal?.querySelector('[data-modal-progress]');
        this.progressBar = this.modal?.querySelector('.shoppy-reviews-modal__progress-bar');
        this.progressKnob = this.modal?.querySelector('.shoppy-reviews-modal__progress-knob');
        this.muteBtn = this.modal?.querySelector('[data-modal-mute]');
        this.thumbsPrev = this.modal?.querySelector('[data-thumbs-prev]');
        this.thumbsNext = this.modal?.querySelector('[data-thumbs-next]');

        this.splide = new Splide(this, {
          type: 'loop',
          focus: 'center',
          perPage: 5,
          perMove: 1,
          gap: '40px',
          padding: 0,
          trimSpace: false,
          pagination: false,
          arrows: false,
          updateOnMove: true,
          speed: 450,
          rewind: false,
          breakpoints: {
            999: {
              perPage: 3,
              gap: '32px',
              padding: 0,
            },
            699: {
              perPage: 1,
              focus: 'center',
              fixedWidth: '70%',
              gap: '22px',
              padding: 0,
            },
          },
        });

        this.splide.on('mounted moved', () => this._onMoved());
        this.splide.on('click', (slide) => this._openModal(slide.index));

        this.prevBtn?.addEventListener('click', () => this.splide.go('<'));
        this.nextBtn?.addEventListener('click', () => this.splide.go('>'));

        this._onVisibility = this._onVisibility.bind(this);
        this._onBlockSelect = this._onBlockSelect.bind(this);
        this._onModalKeydown = this._onModalKeydown.bind(this);
        this._onCartChange = this._onCartChange.bind(this);
        document.addEventListener('visibilitychange', this._onVisibility);
        document.addEventListener('shopify:block:select', this._onBlockSelect);

        this._intersectionObserver = new IntersectionObserver(
          (entries) => {
            this._inView = entries.some((entry) => entry.isIntersecting);
            if (this._modalOpen) return;
            if (this._inView) {
              this._playActive();
            } else {
              this._pauseAll();
              this._clearAdvance();
            }
          },
          { threshold: 0.35 }
        );
        this._intersectionObserver.observe(this);

        this._bindModal();
        this.splide.mount();
        this._bindDurations();
        this.classList.add('is-ready');
      }

      disconnectedCallback() {
        this._clearAdvance();
        this._pauseAll();
        this._intersectionObserver?.disconnect();
        document.removeEventListener('visibilitychange', this._onVisibility);
        document.removeEventListener('shopify:block:select', this._onBlockSelect);
        document.removeEventListener('keydown', this._onModalKeydown);
        document.removeEventListener('cart:change', this._onCartChange);
        document.documentElement.removeEventListener('cart:change', this._onCartChange);
        this.splide?.destroy(true);
        if (this.modal?.open) this.modal.close();
      }

      _reviewSlides() {
        return Array.from(this.querySelectorAll('.shoppy-reviews__slide:not(.splide__slide--clone)'));
      }

      _bindDurations() {
        this.querySelectorAll('.shoppy-reviews__video').forEach((video) => {
          if (video.dataset.durationBound === 'true') return;
          video.dataset.durationBound = 'true';

          const apply = () => this._applyVideoDuration(video);
          if (Number.isFinite(video.duration) && video.duration > 0) apply();
          else video.addEventListener('loadedmetadata', apply, { once: true });
        });
      }

      _applyVideoDuration(video) {
        const label = video.closest('.shoppy-reviews__slide')?.querySelector('.shoppy-reviews__duration');
        if (!label) return;

        const text = this._formatDuration(video.duration);
        label.textContent = text;
        label.hidden = !text;
      }

      _formatDuration(seconds) {
        if (!Number.isFinite(seconds) || seconds <= 0) return '';
        const total = Math.round(seconds);
        const mins = Math.floor(total / 60);
        const secs = total % 60;
        return `${String(mins).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
      }

      _bindModal() {
        if (!this.modal) return;

        this.modal.addEventListener('close', () => this._onModalClosed());
        this.modal.addEventListener('click', (event) => this._onModalClick(event));
        document.addEventListener('cart:change', this._onCartChange);
        document.documentElement.addEventListener('cart:change', this._onCartChange);

        this.modal.querySelectorAll('.shoppy-reviews-modal__form').forEach((form) => {
          form.addEventListener(
            'submit',
            (event) => {
              event.preventDefault();
              event.stopImmediatePropagation();
              this._addToCart(form);
            },
            true
          );
        });

        this.modalVideo?.addEventListener('timeupdate', () => this._updateProgress());
        this.modalVideo?.addEventListener('ended', () => this._goNext());
        this.modalVideo?.addEventListener('play', () => this._syncPlaybackUi());
        this.modalVideo?.addEventListener('pause', () => this._syncPlaybackUi());

        this.progressEl?.addEventListener('pointerdown', (event) => this._seekFromEvent(event));

        const player = this.modal.querySelector('.shoppy-reviews-modal__player');
        if (player) {
          this._onPlayerTouchStart = this._onPlayerTouchStart.bind(this);
          this._onPlayerTouchEnd = this._onPlayerTouchEnd.bind(this);
          this._onPlayerWheel = this._onPlayerWheel.bind(this);
          player.addEventListener('touchstart', this._onPlayerTouchStart, { passive: true });
          player.addEventListener('touchend', this._onPlayerTouchEnd, { passive: true });
          player.addEventListener('wheel', this._onPlayerWheel, { passive: false });
        }
      }

      _onModalClick(event) {
        const closeBtn = event.target.closest('[data-modal-close]');
        if (closeBtn) {
          this.modal.close();
          return;
        }

        if (event.target.closest('[data-modal-mute]')) {
          this._toggleMute();
          return;
        }

        const quickAtc = event.target.closest('[data-quick-atc]');
        if (quickAtc) {
          if (!quickAtc.disabled) this._quickAdd(quickAtc);
          return;
        }

        if (event.target.closest('[data-product-toggle]')) {
          this._toggleProductSheet();
          return;
        }

        const thumb = event.target.closest('[data-review-index]');
        if (thumb && this.modal.contains(thumb)) {
          this._showReview(Number(thumb.dataset.reviewIndex));
          return;
        }

        if (event.target.closest('[data-thumbs-prev]')) {
          this._goPrev();
          return;
        }

        if (event.target.closest('[data-thumbs-next]')) {
          this._goNext();
          return;
        }

        const gallery = event.target.closest('[data-gallery]');
        if (gallery) {
          if (event.target.closest('[data-gallery-next]')) this._scrollGallery(gallery, 1);
          if (event.target.closest('[data-gallery-prev]')) this._scrollGallery(gallery, -1);
          return;
        }

        if (event.target.closest('.shoppy-reviews-modal__player') && !event.target.closest('[data-modal-progress], [data-modal-mute], .shoppy-reviews-modal__actions')) {
          this._toggleModalPlayback();
        }
      }

      _onCartChange(event) {
        if (!this._modalOpen) return;
        if (event.detail?.baseEvent && event.detail.baseEvent !== 'variant:add') return;
        this._closeModal();
      }

      _closeModal() {
        if (!this.modal) return;
        if (typeof this.modal.close === 'function' && this.modal.open) {
          this.modal.close();
          return;
        }
        this.modal.removeAttribute('open');
        this._onModalClosed();
      }

      _onModalKeydown(event) {
        if (!this._modalOpen) return;
        if (event.key === 'ArrowDown' || event.key === 'ArrowRight') this._goNext();
        if (event.key === 'ArrowUp' || event.key === 'ArrowLeft') this._goPrev();
      }

      _openModal(index) {
        if (!this.modal) return;

        this._modalOpen = true;
        this._pauseAll();
        this._clearAdvance();
        this._showReview(index, true);

        if (typeof this.modal.showModal === 'function') {
          if (!this.modal.open) this.modal.showModal();
        } else {
          this.modal.setAttribute('open', '');
        }

        document.documentElement.classList.add('shoppy-reviews-modal-open');
        document.addEventListener('keydown', this._onModalKeydown);
        this._revealProductPanel();
      }

      _onModalClosed() {
        this._modalOpen = false;
        this.modalVideo?.pause();
        this.modal.classList.remove('has-product', 'is-product-expanded');
        document.documentElement.classList.remove('shoppy-reviews-modal-open');
        document.removeEventListener('keydown', this._onModalKeydown);

        if (this._inView && document.visibilityState !== 'hidden') {
          this._playActive();
        }
      }

      _showReview(index, force = false) {
        const slides = this._reviewSlides();
        if (!slides.length) return;

        const last = slides.length - 1;
        const nextIndex = Math.max(0, Math.min(last, index));
        if (!force && nextIndex === this._modalIndex && this.modalVideo?.getAttribute('src')) {
          this._syncSlideNav(last);
          return;
        }

        this._modalIndex = nextIndex;
        const slide = slides[this._modalIndex];

        this.modal.querySelectorAll('.shoppy-reviews-modal__thumb').forEach((thumb, i) => {
          thumb.classList.toggle('is-active', i === this._modalIndex);
        });
        this._scrollActiveThumbIntoView();
        this._syncSlideNav(last);

        this._syncProductPanel(this.modal.open);

        const src = slide.dataset.videoSrc;
        const poster = slide.dataset.poster || '';
        if (this.modalVideo) {
          this.modalVideo.pause();
          this.modalVideo.poster = poster;
          if (src) {
            if (this.modalVideo.getAttribute('src') !== src) {
              this.modalVideo.src = src;
            }
            this.modalVideo.currentTime = 0;
            this._playModalVideo();
          }
        }

        this._setProgress(0);
        this.splide.go(this._modalIndex);
      }

      _goNext() {
        this._showReview(this._modalIndex + 1);
      }

      _goPrev() {
        this._showReview(this._modalIndex - 1);
      }

      _syncSlideNav(last) {
        const atStart = this._modalIndex <= 0;
        const atEnd = this._modalIndex >= last;
        this.thumbsPrev?.toggleAttribute('disabled', atStart);
        this.thumbsNext?.toggleAttribute('disabled', atEnd);
      }

      _onPlayerTouchStart(event) {
        const touch = event.changedTouches?.[0];
        this._touchStartX = touch?.clientX ?? 0;
        this._touchStartY = touch?.clientY ?? 0;
        this._touchOnControls = Boolean(
          event.target.closest('[data-modal-mute], [data-modal-progress], .shoppy-reviews-modal__actions, .shoppy-reviews-modal__sidebar')
        );
      }

      _onPlayerTouchEnd(event) {
        if (this._touchOnControls) return;

        const touch = event.changedTouches?.[0];
        const deltaX = this._touchStartX - (touch?.clientX ?? this._touchStartX);
        const deltaY = this._touchStartY - (touch?.clientY ?? this._touchStartY);
        const absX = Math.abs(deltaX);
        const absY = Math.abs(deltaY);

        if (Math.max(absX, absY) >= 56) {
          this._suppressPlayerClick = true;
          const goNext = absX > absY ? deltaX > 0 : deltaY > 0;
          if (goNext) this._goNext();
          else this._goPrev();
          return;
        }

        this._toggleModalPlayback();
        this._suppressPlayerClick = true;
      }

      _onPlayerWheel(event) {
        if (Math.abs(event.deltaY) < 24) return;
        event.preventDefault();
        const now = Date.now();
        if (this._wheelLock && now - this._wheelLock < 450) return;
        this._wheelLock = now;
        if (event.deltaY > 0) this._goNext();
        else this._goPrev();
      }

      _playModalVideo() {
        if (!this.modalVideo) return;

        this.modalVideo.playsInline = true;
        this.modalVideo.muted = this._modalMuted;
        this._syncMuteUi();

        const tryPlay = () => {
          const playPromise = this.modalVideo.play();
          if (playPromise && typeof playPromise.then === 'function') {
            playPromise.catch(() => {
              this._modalMuted = true;
              this.modalVideo.muted = true;
              this._syncMuteUi();
              this.modalVideo.play().catch(() => {});
            });
          }
        };

        if (this.modalVideo.readyState >= 2) {
          tryPlay();
        } else {
          this.modalVideo.addEventListener('canplay', tryPlay, { once: true });
          this.modalVideo.load();
        }
      }

      _toggleMute() {
        this._modalMuted = !this._modalMuted;
        if (this.modalVideo) this.modalVideo.muted = this._modalMuted;
        this._syncMuteUi();
      }

      _toggleModalPlayback() {
        if (this._suppressPlayerClick) {
          this._suppressPlayerClick = false;
          return;
        }
        if (!this.modalVideo) return;

        if (this.modalVideo.paused) this._playModalVideo();
        else this.modalVideo.pause();
      }

      _syncPlaybackUi() {
        this.modal?.querySelector('.shoppy-reviews-modal__player')?.classList.toggle(
          'is-paused',
          Boolean(this.modalVideo?.paused)
        );
      }

      _syncProductPanel(animate) {
        const panel = this.modal.querySelector(`.shoppy-reviews-modal__product[data-index="${this._modalIndex}"]`);
        this.modal.querySelectorAll('.shoppy-reviews-modal__product').forEach((item) => {
          item.hidden = item !== panel;
        });
        this.modal.classList.remove('is-product-expanded');
        this._syncProductToggle(false);

        if (animate) {
          this.modal.classList.toggle('has-product', Boolean(panel));
          if (panel) this._initGallery(panel);
        } else {
          this.modal.classList.remove('has-product');
        }
      }

      _revealProductPanel() {
        const reveal = () => {
          const panel = this.modal.querySelector(`.shoppy-reviews-modal__product[data-index="${this._modalIndex}"]`);
          this.modal.classList.toggle('has-product', Boolean(panel));
          if (panel) this._initGallery(panel);
        };

        requestAnimationFrame(() => requestAnimationFrame(reveal));
      }

      _toggleProductSheet() {
        const expanded = this.modal.classList.toggle('is-product-expanded');
        this._syncProductToggle(expanded);
        if (expanded) {
          const panel = this.modal.querySelector(`.shoppy-reviews-modal__product[data-index="${this._modalIndex}"]`);
          if (panel) this._initGallery(panel);
        }
      }

      _syncProductToggle(expanded) {
        this.modal.querySelectorAll('[data-product-toggle]').forEach((button) => {
          if (button.hasAttribute('aria-expanded')) {
            button.setAttribute('aria-expanded', expanded ? 'true' : 'false');
          }
        });
      }

      _quickAdd(button) {
        const item = button.closest('[data-product-item]');
        const formId = item?.dataset.formId;
        const form =
          (formId && this.modal.querySelector(`[id="${formId}"]`)) ||
          item?.querySelector('form') ||
          button.closest('.shoppy-reviews-modal__product')?.querySelector('form');
        if (form) this._addToCart(form, button);
      }

      async _addToCart(form, button) {
        if (this._addingToCart || !form) return;

        const variantInput = form.querySelector('[name="id"]');
        if (!variantInput?.value) return;

        this._addingToCart = true;
        button?.setAttribute('disabled', 'disabled');

        try {
          const root = window.Shopify?.routes?.root || '/';
          const sectionsToBundle = ['variant-added'];
          document.documentElement.dispatchEvent(
            new CustomEvent('cart:prepare-bundled-sections', {
              bubbles: true,
              detail: { sections: sectionsToBundle },
            })
          );

          const formData = new FormData(form);
          formData.set('sections', sectionsToBundle.join(','));
          formData.set('sections_url', `${root}variants/${variantInput.value}`);

          const response = await fetch(`${root}cart/add.js`, {
            method: 'POST',
            headers: {
              'X-Requested-With': 'XMLHttpRequest',
              Accept: 'application/json',
            },
            body: formData,
          });
          const responseJson = await response.json();

          if (!response.ok) return;

          const cart = await (await fetch(`${root}cart.js`)).json();
          cart.sections = responseJson.sections;

          document.documentElement.dispatchEvent(
            new CustomEvent('cart:change', {
              bubbles: true,
              detail: {
                baseEvent: 'variant:add',
                cart,
              },
            })
          );

          this._closeModal();
        } catch (error) {
          console.debug('[shoppy-reviews] add to cart failed', error);
        } finally {
          this._addingToCart = false;
          button?.removeAttribute('disabled');
        }
      }

      _initGallery(panel) {
        const scroller = panel.querySelector('[data-gallery-scroll]');
        if (scroller) scroller.scrollLeft = 0;
      }

      _scrollGallery(gallery, direction) {
        const scroller = gallery.querySelector('[data-gallery-scroll]');
        if (!scroller) return;

        const images = Array.from(scroller.querySelectorAll('img'));
        if (!images.length) return;

        const current = images.reduce((closest, img, index) => {
          const dist = Math.abs(img.offsetLeft - scroller.scrollLeft);
          return dist < closest.dist ? { index, dist } : closest;
        }, { index: 0, dist: Infinity }).index;

        const next = Math.min(images.length - 1, Math.max(0, current + direction));
        scroller.scrollTo({ left: images[next].offsetLeft, behavior: 'smooth' });
      }

      _syncMuteUi() {
        this.muteBtn?.classList.toggle('is-muted', this._modalMuted);
        this.muteBtn?.setAttribute('aria-pressed', this._modalMuted ? 'true' : 'false');
        this.muteBtn?.setAttribute('aria-label', this._modalMuted ? 'Unmute' : 'Mute');
      }

      _updateProgress() {
        if (!this.modalVideo?.duration) return;
        this._setProgress((this.modalVideo.currentTime / this.modalVideo.duration) * 100);
      }

      _setProgress(value) {
        const percent = Math.min(100, Math.max(0, value));
        if (this.progressBar) this.progressBar.style.width = `${percent}%`;
        if (this.progressKnob) this.progressKnob.style.left = `${percent}%`;
      }

      _seekFromEvent(event) {
        if (!this.modalVideo?.duration) return;
        const rect = this.progressEl.getBoundingClientRect();
        const ratio = Math.min(1, Math.max(0, (event.clientX - rect.left) / rect.width));
        this.modalVideo.currentTime = ratio * this.modalVideo.duration;
        this._updateProgress();
      }

      _scrollActiveThumbIntoView() {
        const active = this.modal.querySelector('.shoppy-reviews-modal__thumb.is-active');
        active?.scrollIntoView({ block: 'nearest', inline: 'nearest' });
      }

      _onMoved() {
        if (this._modalOpen) return;
        this._pauseAll();
        if (this._inView && document.visibilityState !== 'hidden') {
          this._playActive();
        }
      }

      _activeSlide() {
        return this.splide?.Components.Slides.getAt(this.splide.index)?.slide;
      }

      _playActive() {
        this._clearAdvance();
        if (this._modalOpen) return;

        const slide = this._activeSlide();
        if (!slide) return;

        const video = slide.querySelector('.shoppy-reviews__video');
        if (!video || !video.querySelector('source')) {
          this._scheduleAdvance(5000);
          return;
        }

        video.muted = true;
        video.playsInline = true;

        const tryPlay = () => {
          const playPromise = video.play();
          if (playPromise && typeof playPromise.then === 'function') {
            playPromise
              .then(() => slide.classList.add('is-playing'))
              .catch(() => {
                slide.classList.remove('is-playing');
                this._scheduleAdvance(5000);
              });
          }
        };

        if (video.readyState >= 2) {
          tryPlay();
        } else {
          video.addEventListener('canplay', tryPlay, { once: true });
          video.load();
        }

        video.onended = () => this.splide.go('>');
      }

      _pauseAll() {
        this.querySelectorAll('.shoppy-reviews__video').forEach((video) => {
          video.onended = null;
          video.pause();
        });

        this.querySelectorAll('.shoppy-reviews__slide.is-playing').forEach((slide) => {
          slide.classList.remove('is-playing');
        });
      }

      _scheduleAdvance(delay) {
        this._clearAdvance();
        if (this._modalOpen || !this.splide || this.splide.length < 2) return;
        this._advanceTimer = window.setTimeout(() => this.splide.go('>'), delay);
      }

      _clearAdvance() {
        if (this._advanceTimer) {
          window.clearTimeout(this._advanceTimer);
          this._advanceTimer = null;
        }
      }

      _onVisibility() {
        if (document.visibilityState === 'hidden') {
          this._pauseAll();
          this._clearAdvance();
          this.modalVideo?.pause();
          return;
        }

        if (this._modalOpen) {
          this._playModalVideo();
          return;
        }

        if (this._inView) this._playActive();
      }

      _onBlockSelect(event) {
        const slide = event.target.closest?.('.shoppy-reviews__slide');
        if (!slide || !this.contains(slide) || slide.classList.contains('splide__slide--clone')) return;

        const slides = this._reviewSlides();
        const index = slides.indexOf(slide);
        if (index >= 0) this.splide.go(index);
      }
    }
  );
}
